import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftRight,
  Copy,
  CreditCard,
  EllipsisVertical,
  Eye,
  EyeOff,
  Landmark,
  Power,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'sonner'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGenerateVban } from 'components/hooks/useGenerateVban'
import { useGetVbanDetails } from 'components/hooks/useGetVbanDetails'
import { Banner, Button, DropdownMenu, IconButton, Tooltip, TooltipProvider } from 'slab'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
  useGetBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate,
} from 'utils/web_api'
import {
  type BrokersEnrollmentEnriched,
  type BrokersPaginatedStripeIssuingCardsList,
  type BrokersStripeIssuingCard,
} from 'utils/web_api.schemas'

import EnrollmentPaymentMethodRemapSheet from './EnrollmentPaymentMethodRemapSheet'
import StripeCardDetailsModal from './StripeCardDetailsModal'

interface EnrollmentPaymentMethodProps {
  memberId: string
  enrollment: BrokersEnrollmentEnriched
}

const PaymentMethodDropdownMenu = ({
  memberId,
  cardId,
  isActive = false,
  selectedEnrollment,
}: {
  memberId: string
  cardId?: string
  isActive?: boolean
  selectedEnrollment: BrokersEnrollmentEnriched
}) => {
  const queryClient = useQueryClient()
  const [isRemapSheetOpen, setIsRemapSheetOpen] = useState(false)

  const activateCardMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate({
      mutation: {
        onSuccess: (updatedCard: BrokersStripeIssuingCard) => {
          toast.success('Card activated successfully', {
            position: 'top-center',
          })

          // Update the cards cache with the reactivated card
          const cardsQueryKey =
            getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
              memberId,
              undefined
            )
          queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
            cardsQueryKey,
            (oldData) => {
              if (!oldData) return oldData

              return {
                ...oldData,
                data:
                  oldData.data?.map((card) =>
                    card.id === updatedCard.id ? updatedCard : card
                  ) || [],
              }
            }
          )
        },
        onError: (error: any) => {
          toast.error(
            `Failed to activate card: ${error?.message || 'Unknown error'}`,
            {
              position: 'top-center',
            }
          )
        },
      },
    })

  const handleReactivateCard = (cardId: string) => {
    activateCardMutation.mutate({
      memberId,
      cardId,
    })
  }

  return (
    <>
      <DropdownMenu
        align='end'
        trigger={
          <IconButton
            label='Card settings'
            variant='secondary'
            icon={EllipsisVertical}
          />
        }
      >
        <DropdownMenu.Item
          leftIcon={ArrowLeftRight}
          onSelect={() => setIsRemapSheetOpen(true)}
        >
          Change payment method
        </DropdownMenu.Item>

        {!isActive && cardId && (
          <DropdownMenu.Item
            leftIcon={Power}
            onSelect={() => handleReactivateCard(cardId)}
          >
            Reactivate card
          </DropdownMenu.Item>
        )}
      </DropdownMenu>

      <EnrollmentPaymentMethodRemapSheet
        memberId={memberId}
        selectedEnrollment={selectedEnrollment}
        isOpen={isRemapSheetOpen}
        onOpenChange={setIsRemapSheetOpen}
      />
    </>
  )
}

const EnrollmentPaymentMethod: React.FC<EnrollmentPaymentMethodProps> = ({
  memberId,
  enrollment,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [isVbanRevealed, setIsVbanRevealed] = useState(false)
  const queryClient = useQueryClient()
  const { isUserThatchAdmin, stripePublishableKey } = useContext(BrokersMemberContext)

  const employerHasBangorAccount = (enrollment as any).employer_has_bangor_account === true
  const hasVban = (enrollment as any).has_vban === true
  const isBangorEmployer = (enrollment as any).is_bangor_employer === true
  const vbanMaskedAccountNumber = (enrollment as any).vban_masked_account_number as string | undefined
  const vbanMaskedRoutingNumber = (enrollment as any).vban_masked_routing_number as string | undefined

  const { data: stripeCards, isLoading, error } = useGetBrokersMembersMemberIdStripeIssuingCards(
    memberId,
    undefined,
    {
      query: {
        enabled: !!memberId
      },
    }
  )

  const generateVbanMutation = useGenerateVban({
    enrollmentId: enrollment.id,
  })

  const createCardMutation = usePostBrokersMembersMemberIdStripeIssuingCards({
    mutation: {
      onSuccess: (newCard) => {
        toast.success('Card successfully created', {
          position: 'top-center',
        })

        // Update the cards cache with the new card
        const cardsQueryKey =
          getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
            memberId,
            undefined
          )
        queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
          cardsQueryKey,
          (oldData) => {
            if (!oldData) {
              return {
                data: [newCard],
                pagination: undefined,
              }
            }

            return {
              ...oldData,
              data: [newCard, ...(oldData.data || [])],
            }
          }
        )

        // Invalidate enrollments query since the enrollment's payment method fields will be updated
        queryClient.invalidateQueries({
          queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
            enrollment.id
          ),
        })
      },
      onError: (error: GrapeAPIErrorType) => {
        toast.error(
          `Failed to create card: ${error?.errors?.[0] || 'Unknown error'}`,
          {
            position: 'top-center',
          }
        )
      },
    },
  })

  const handleShowCard = (cardId: string) => {
    if (!stripePublishableKey) {
      toast.error('Stripe configuration is missing. Please contact support.', {
        position: 'top-center',
      })
      return
    }
    setSelectedCardId(cardId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedCardId(null)
  }

  const handleCreateCard = () => {
    createCardMutation.mutate({
      memberId,
      data: {
        enrollment_id: enrollment.id,
      },
    })
  }

  const handleGenerateVban = () => {
    generateVbanMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('VBAN successfully generated', {
          position: 'top-center',
        })
        queryClient.invalidateQueries({
          queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
            enrollment.id
          ),
        })
      },
      onError: (error: GrapeAPIErrorType) => {
        toast.error(
          `Failed to generate VBAN: ${error?.message || error?.errors?.[0] || 'Unknown error'}`,
          {
            position: 'top-center',
          }
        )
      },
    })
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard`, {
      position: 'top-center',
    })
  }

  if (error) {
    const errorMessage = (error as GrapeAPIErrorType)?.errors?.[0] || 'Unknown error'
    return (
      <div className='mb-6'>
        <Banner type='critical' title={`Error fetching card details: ${errorMessage}`} />
      </div>
    )
  }

  const renderLoadingSkeleton = () => {
    return (
      <div className='mb-6'>
        <div className='text-sm text-gray-600 mb-2'>
          <div className='h-4 bg-gray-200 rounded w-32 animate-pulse' />
        </div>
        <div className='flex items-center gap-2'>
          <div className='h-10 bg-gray-200 rounded w-full animate-pulse' />
          {isUserThatchAdmin && (
            <div className='h-10 w-10 bg-gray-200 rounded animate-pulse' />
          )}
        </div>
      </div>
    )
  }

  const StripeCardDisplay = ({
    stripeCard,
  }: {
    stripeCard: BrokersStripeIssuingCard
  }) => {
    // The below logic assumes if a card is not active or inactive, it is canceled.
    const isActive = stripeCard.remote_status === 'active'
    const isInactive = stripeCard.remote_status === 'inactive'

    const buttonText = isActive
      ? `Show ••••${stripeCard.last4}`
      : isInactive
        ? `Inactive ••••${stripeCard.last4}`
        : `Canceled ••••${stripeCard.last4}`

    return (
      <Button
        variant='secondary'
        width='full'
        onClick={isActive ? () => handleShowCard(stripeCard.id) : undefined}
        id={isActive ? `show-card-${enrollment.id}` : undefined}
        disabled={!isActive}
        leftIcon={CreditCard}
      >
        {isActive ? buttonText : <span className='text-sm'>{buttonText}</span>}
      </Button>
    )
  }

  const VbanDisplay = () => {
    const {
      isLoading: isLoadingVbanDetails,
    } = useGetVbanDetails({
      enrollmentId: enrollment.id,
      enabled: isVbanRevealed,
    })

    const handleRevealToggle = () => {
      setIsVbanRevealed(!isVbanRevealed)
    }

    return (
      <Button
        variant='secondary'
        width='full'
        onClick={handleRevealToggle}
        disabled={isLoadingVbanDetails}
        leftIcon={isVbanRevealed ? EyeOff : Eye}
      >
        {isLoadingVbanDetails
          ? 'Loading...'
          : isVbanRevealed
            ? 'Hide VBAN Details'
            : 'View VBAN Details'}
      </Button>
    )
  }

  const VbanDetailsDisplay = () => {
    const {
      data: vbanDetails,
      isLoading: isLoadingVbanDetails,
      error: vbanDetailsError,
    } = useGetVbanDetails({
      enrollmentId: enrollment.id,
      enabled: isVbanRevealed,
    })

    if (!isVbanRevealed) return null

    const displayAccountNumber = vbanDetails
      ? vbanDetails.account_number
      : vbanMaskedAccountNumber
    const displayRoutingNumber = vbanDetails
      ? vbanDetails.routing_number
      : vbanMaskedRoutingNumber

    const canCopy = vbanDetails && !isLoadingVbanDetails

    return (
      <div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4'>
        {vbanDetailsError && (
          <div className='text-xs text-red-600 mb-2'>
            Failed to load details. Please try again.
          </div>
        )}
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <div>
              <div className='text-xs text-gray-500 uppercase tracking-wide'>Financial Institution</div>
              <div className='font-mono text-sm font-medium'>Bangor Savings Bank</div>
            </div>
          </div>
          <div className='flex items-center justify-between'>
            <div>
              <div className='text-xs text-gray-500 uppercase tracking-wide'>Account Number</div>
              <div className='font-mono text-sm font-medium'>{displayAccountNumber}</div>
            </div>
            <IconButton
              label='Copy account number'
              variant='secondary'
              icon={Copy}
              onClick={() => copyToClipboard(vbanDetails!.account_number, 'Account number')}
              disabled={!canCopy}
            />
          </div>
          <div className='flex items-center justify-between'>
            <div>
              <div className='text-xs text-gray-500 uppercase tracking-wide'>Routing Number</div>
              <div className='font-mono text-sm font-medium'>{displayRoutingNumber}</div>
            </div>
            <IconButton
              label='Copy routing number'
              variant='secondary'
              icon={Copy}
              onClick={() => copyToClipboard(vbanDetails!.routing_number, 'Routing number')}
              disabled={!canCopy}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentMethod = () => {
    // Show loading skeleton while fetching cards
    if (isLoading) {
      return renderLoadingSkeleton()
    }

    // Reimbursement only.
    if (enrollment.reimbursement_only) {
      return (
        <div className='mb-6'>
          <div className='text-sm text-gray-600 mb-2'>
            <span className='font-semibold'>Payment Method:</span> Reimbursement
            Only: Member Pays
          </div>
        </div>
      )
    }

    const hasStripeCard = enrollment.payment_method_type === 'Stripe Card' && stripeCards?.data
    const stripeCard = hasStripeCard
      ? stripeCards.data?.find((card) => card.id === enrollment.payment_method_id)
      : null

    const shouldShowVbanButton = isBangorEmployer && !hasVban
    const shouldShowStripeCardButton = !stripeCard

    // Determine payment method label text
    const getPaymentMethodLabel = () => {
      if (stripeCard) {
        const statusMap: Record<string, string> = {
          active: 'Stripe Card',
          inactive: 'Stripe Card (Inactive)',
          canceled: 'Stripe Card (Canceled)'
        }
        return statusMap[stripeCard.remote_status] || 'Stripe Card (Canceled)'
      }

      if (hasVban) return 'ACH / VBAN'

      if (shouldShowStripeCardButton && shouldShowVbanButton) {
        return 'Stripe Card / ACH / VBAN'
      }

      if (shouldShowStripeCardButton) return 'Stripe Card'
      if (shouldShowVbanButton) return 'ACH / VBAN'

      return null
    }

    const paymentMethodLabel = getPaymentMethodLabel()

    const stripeCardButton = stripeCard
      ? (
        <StripeCardDisplay stripeCard={stripeCard} />
        )
      : shouldShowStripeCardButton
        ? (
          <Button
            variant='primary'
            width='full'
            onClick={handleCreateCard}
            id={`create-card-${enrollment.id}`}
            disabled={createCardMutation.isPending}
          >
            {createCardMutation.isPending ? 'Creating...' : 'Create card'}
          </Button>
          )
        : null

    const vbanButton = hasVban
      ? (
        <VbanDisplay />
        )
      : shouldShowVbanButton
        ? (
          <TooltipProvider>
            <Tooltip
              content='Pending final account creation'
              enabled={!employerHasBangorAccount}
            >
              <Button
                variant='primary'
                width='full'
                onClick={handleGenerateVban}
                id={`generate-vban-${enrollment.id}`}
                disabled={generateVbanMutation.isPending || !employerHasBangorAccount}
                leftIcon={Landmark}
              >
                {generateVbanMutation.isPending ? 'Generating...' : 'Generate VBAN'}
              </Button>
            </Tooltip>
          </TooltipProvider>
          )
        : null

    return (
      <div className='mb-6'>
        {paymentMethodLabel && (
          <div className='text-sm text-gray-600 mb-2'>
            <span className='font-semibold'>Payment Method:</span> {paymentMethodLabel}
          </div>
        )}
        <div className='flex items-start gap-2'>
          {stripeCardButton && (
            <div className='flex-1'>
              {stripeCardButton}
            </div>
          )}
          {vbanButton && (
            <div className='flex-1'>
              {vbanButton}
            </div>
          )}
          {isUserThatchAdmin && (
            <div className='flex-shrink-0'>
              <PaymentMethodDropdownMenu
                memberId={memberId}
                cardId={stripeCard?.id}
                isActive={stripeCard?.remote_status === 'active'}
                selectedEnrollment={enrollment}
              />
            </div>
          )}
        </div>
        {hasVban && <VbanDetailsDisplay />}
      </div>
    )
  }

  return (
    <div>
      {renderPaymentMethod()}

      {/* Modal for showing card details */}
      {isModalOpen && selectedCardId && stripePublishableKey && (
        <StripeCardDetailsModal
          memberId={memberId}
          cardId={selectedCardId}
          enrollmentId={enrollment.id}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          stripePublishableKey={stripePublishableKey!}
        />
      )}
    </div>
  )
}

export default EnrollmentPaymentMethod
